import type { AppContext } from "@/types/scheduling";

import { findNutritionAreaBySynonym } from "@/apps/appointment-scheduler/utils";
import { getParams } from "@/utils/app";
import { businessIDs } from "@/utils/businessLeads";
import { bannedStates, nutritionAreas, paymentMethods, usaStates } from "@/utils/constants";
import { getStage } from "@/utils/functions";
import { produce } from "immer";
import { dietitianListStaging, dietitianListWInsurance, insuranceList } from "shared/constants";

export const handleGetOverwriteOptions = <T extends AppContext>(context: T) => {
  return produce(context, (draft) => {
    if (draft.available_expertise_areas && draft.available_expertise_areas.length > 0) {
      draft.nutrition_area_options = nutritionAreas.filter((area) =>
        draft.available_expertise_areas?.some(
          (available_aon) => findNutritionAreaBySynonym(available_aon, nutritionAreas) === area.value,
        ),
      );
    }
    if (draft.available_states && draft.available_states.length > 0) {
      draft.state_options = usaStates.filter(
        (state) => context.available_states?.includes(state.value) && !bannedStates.includes(state.value),
      );
    }
    /**
     * TODO we need this whole thing to be certain about showing insurance payment method as an option
     * */
    if (draft.provider_id !== "" && draft.flow.name === "rde_profile_flow") {
      const isDev = process.env.NODE_ENV === "development";
      const stage = getStage(isDev);

      const insuranceEligibleProviders = stage === "prod" ? dietitianListWInsurance : dietitianListStaging;

      // explicitly determine if provider accepts insurance
      const providerAcceptsInsurance = insuranceEligibleProviders.some(
        (provider) => provider.healthieId === draft.provider_id,
      );

      // filter payment methods based on RD elligibility for insurance
      draft.payment_method_options = paymentMethods.filter(
        (paymentMethod) => paymentMethod.value !== "insurance" || providerAcceptsInsurance,
      );
    }

    // check for existing params
    const params = getParams();
    const { lead_name, lead_email, lead_phone_number, insurance_provider_other } = params;

    if (lead_name?.length) {
      draft.next_allowed.lead_name = true;
    }
    if (lead_email?.length) {
      draft.next_allowed.lead_email = true;
    }
    if (lead_phone_number?.length) {
      draft.next_allowed.lead_phone_number = true;
    }
    if (insurance_provider_other?.length) {
      draft.next_allowed.insurance_selection_other = true;
    }
    if (draft.result.business_name === businessIDs.sworn) {
      draft.result.payment_method = {
        value: "insurance",
        label: "Insurance",
        key: "payment_method",
      };

      if (draft.insurance_provider_options && draft.insurance_provider_options.length > 0) {
        draft.insurance_provider_options = insuranceList.filter(
          (insurance) => insurance.value !== "other" && insurance.visible,
        );
      }
    }
  });
};
